import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Container, Table, Button, Modal, Form, Badge, Spinner, Alert,
  InputGroup, Nav, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import {
  PlusCircle, Trash, Download, Upload, Search, XCircle, Globe,
} from 'react-bootstrap-icons';
import EMMSectionHeader from './EMMSectionHeader';
import { useGetUserPreferencesQuery, useUpdateUserPreferencesMutation } from '../services/api';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../hooks/useTenantIdForPreferences';

interface BlocklistEntry {
  id: string;
  value: string;
  type: 'email' | 'domain';
  addedAt: string;
  source: string;
}

interface BlocklistSettings {
  autoBlockUnsubscribes: boolean;
  blockLeadStatuses: string[];
  blockReplyText: string;
}

const DEFAULT_LEAD_STATUSES = [
  'Not interested',
  'Do not contact',
  'Wrong person',
  'Out of office',
  'Unsubscribed',
  'Bounced',
];

const DEFAULT_SETTINGS: BlocklistSettings = {
  autoBlockUnsubscribes: true,
  blockLeadStatuses: [],
  blockReplyText: '',
};

function genId(): string {
  return `bl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'numeric', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
}

function isValidEmail(val: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
}

function isValidDomain(val: string): boolean {
  return /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(val);
}

function getBlockSourceLabel(source: unknown): string {
  const normalized = String(source || '').trim().toLowerCase();
  if (normalized === 'system_hard_bounce') return 'Hard bounce (automatic)';
  if (normalized === 'bounced-list') return 'Bounce List (manual)';
  if (normalized === 'csv-import') return 'CSV import';
  if (normalized === 'manual') return 'Manual';
  if (!normalized) return 'Unknown';
  return normalized.replace(/[_-]+/g, ' ');
}

function getBlockSourceBadgeVariant(source: unknown): 'success' | 'light' {
  return String(source || '').trim().toLowerCase() === 'system_hard_bounce' ? 'success' : 'light';
}

export default function BlockList() {
  const tenantId = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();

  const { data: prefsData, isLoading } = useGetUserPreferencesQuery(userPreferencesQueryArg, {
    refetchOnMountOrArgChange: true,
  });
  const [updatePrefs, { isLoading: isSaving }] = useUpdateUserPreferencesMutation();

  const preferences: any = (prefsData as any)?.preferences || {};
  const savedBlocklist: BlocklistEntry[] = useMemo(() => {
    const raw = preferences.blocklist;
    return Array.isArray(raw) ? raw : [];
  }, [preferences.blocklist]);
  const savedSettings: BlocklistSettings = useMemo(() => {
    const raw = preferences.blocklistSettings;
    return raw && typeof raw === 'object' ? { ...DEFAULT_SETTINGS, ...raw } : { ...DEFAULT_SETTINGS };
  }, [preferences.blocklistSettings]);

  const [entries, setEntries] = useState<BlocklistEntry[]>([]);
  const [settings, setSettings] = useState<BlocklistSettings>({ ...DEFAULT_SETTINGS });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (savedBlocklist.length > 0 || (!isLoading && entries.length === 0 && !dirty)) {
      setEntries(savedBlocklist);
      setDirty(false);
    }
  }, [savedBlocklist, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      setSettings({ ...DEFAULT_SETTINGS, ...savedSettings });
    }
  }, [savedSettings, isLoading]);

  const [activeTab, setActiveTab] = useState<'blocklist' | 'triggers'>('blocklist');
  const [searchTerm, setSearchTerm] = useState('');
  const [domainSearch, setDomainSearch] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addError, setAddError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [importPreview, setImportPreview] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [customStatus, setCustomStatus] = useState('');

  const persist = useCallback(
    async (nextEntries: BlocklistEntry[], nextSettings?: BlocklistSettings) => {
      setEntries(nextEntries);
      setDirty(true);
      const payload: any = { blocklist: nextEntries };
      if (nextSettings) {
        setSettings(nextSettings);
        payload.blocklistSettings = nextSettings;
      }
      try {
        await updatePrefs({
          ...preferencesTenantWriteSpread(tenantId),
          preferences: payload,
        }).unwrap();
        setDirty(false);
      } catch (err) {
        console.error('Failed to save blocklist:', err);
      }
    },
    [updatePrefs, tenantId]
  );

  const persistSettings = useCallback(
    async (nextSettings: BlocklistSettings) => {
      setSettings(nextSettings);
      try {
        await updatePrefs({
          ...preferencesTenantWriteSpread(tenantId),
          preferences: { blocklistSettings: nextSettings },
        }).unwrap();
      } catch (err) {
        console.error('Failed to save blocklist settings:', err);
      }
    },
    [updatePrefs, tenantId]
  );

  const filteredEntries = useMemo(() => {
    let result = entries;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (domainSearch) {
        result = result.filter((e) => {
          const domain = e.type === 'domain' ? e.value : e.value.split('@')[1] || '';
          return domain.toLowerCase().includes(term);
        });
      } else {
        result = result.filter((e) => e.value.toLowerCase().includes(term));
      }
    }
    return [...result].sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime());
  }, [entries, searchTerm, domainSearch]);

  const selectAll = filteredEntries.length > 0 && filteredEntries.every((e) => selected.has(e.id));

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredEntries.map((e) => e.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    const val = addValue.trim().toLowerCase();
    if (!val) { setAddError('Enter an email or domain.'); return; }

    const isEmail = isValidEmail(val);
    const isDomain = isValidDomain(val);
    if (!isEmail && !isDomain) { setAddError('Enter a valid email address or domain.'); return; }

    if (entries.some((e) => e.value.toLowerCase() === val)) {
      setAddError('Already in block list.');
      return;
    }

    const entry: BlocklistEntry = {
      id: genId(),
      value: val,
      type: isDomain && !isEmail ? 'domain' : 'email',
      addedAt: new Date().toISOString(),
      source: 'manual',
    };
    persist([entry, ...entries]);
    setAddValue('');
    setAddError('');
    setShowAddModal(false);
  };

  const handleDeleteSelected = () => {
    if (selected.size === 0) return;
    const next = entries.filter((e) => !selected.has(e.id));
    persist(next);
    setSelected(new Set());
  };

  const handleDeleteSingle = (id: string) => {
    persist(entries.filter((e) => e.id !== id));
    setSelected((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const handleDownloadCSV = () => {
    const rows = [['Email/Domain', 'Type', 'Date Added', 'Source']];
    const target = selected.size > 0 ? entries.filter((e) => selected.has(e.id)) : filteredEntries;
    target.forEach((e) => {
      rows.push([e.value, e.type, e.addedAt, e.source]);
    });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blocklist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setImportText(text);
      parseImportPreview(text);
      setShowImportModal(true);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const parseImportPreview = (text: string) => {
    const lines = text.split(/[\r\n]+/).map((l) => l.trim()).filter(Boolean);
    const values: string[] = [];
    const existingValues = new Set(entries.map((e) => e.value.toLowerCase()));

    for (const line of lines) {
      const parts = line.split(',').map((p) => p.replace(/"/g, '').trim().toLowerCase());
      for (const part of parts) {
        if ((isValidEmail(part) || isValidDomain(part)) && !existingValues.has(part) && !values.includes(part)) {
          values.push(part);
        }
      }
    }
    setImportPreview(values);
    setImportError(values.length === 0 ? 'No valid new emails or domains found in file.' : '');
  };

  const handleImportConfirm = () => {
    if (importPreview.length === 0) return;
    const now = new Date().toISOString();
    const newEntries: BlocklistEntry[] = importPreview.map((val) => ({
      id: genId(),
      value: val,
      type: (isValidDomain(val) && !isValidEmail(val) ? 'domain' : 'email') as 'email' | 'domain',
      addedAt: now,
      source: 'csv-import',
    }));
    persist([...newEntries, ...entries]);
    setShowImportModal(false);
    setImportText('');
    setImportPreview([]);
  };

  const handlePasteImport = () => {
    if (!importText.trim()) return;
    parseImportPreview(importText);
  };

  if (isLoading) {
    return (
      <>
        <EMMSectionHeader title="Block List" showHelp />
        <div className="d-flex justify-content-center align-items-center p-5">
          <Spinner animation="border" />
        </div>
      </>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="Block List"
        showHelp
        searchValue={activeTab === 'blocklist' ? searchTerm : undefined}
        onSearchChange={activeTab === 'blocklist' ? setSearchTerm : undefined}
      />

      <Container fluid className="pt-0">
        <Nav variant="underline" activeKey={activeTab} onSelect={(k) => setActiveTab(k as any)} className="px-3 pt-2 bg-white border-bottom">
          <Nav.Item>
            <Nav.Link eventKey="blocklist" style={{ fontSize: '0.85rem' }}>Blocklist</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="triggers" style={{ fontSize: '0.85rem' }}>AI Blocklist Triggers</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === 'blocklist' && (
          <div className="p-3">
            <Alert variant="light" className="py-2">
              Source labels show how an entry got into the Block List. <strong>Hard bounce (automatic)</strong> means runtime enforcement added it automatically after an active tenant hard bounce.
            </Alert>
            {/* Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Form.Check
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  label=""
                  style={{ marginRight: -4 }}
                  disabled={filteredEntries.length === 0}
                />
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Search by domain only</Tooltip>}
                >
                  <Button
                    variant={domainSearch ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setDomainSearch((p) => !p)}
                    style={{ fontSize: '0.75rem', padding: '3px 8px' }}
                  >
                    <Globe size={12} className="me-1" />Domain Search
                  </Button>
                </OverlayTrigger>
                {selected.size > 0 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleDeleteSelected}
                    style={{ fontSize: '0.75rem' }}
                    disabled={isSaving}
                  >
                    <Trash size={12} className="me-1" />Delete ({selected.size})
                  </Button>
                )}
                <span className="text-muted small">
                  {filteredEntries.length} entr{filteredEntries.length !== 1 ? 'ies' : 'y'}
                  {searchTerm && ` matching "${searchTerm}"`}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadCSV}
                  disabled={filteredEntries.length === 0}
                  style={{ fontSize: '0.75rem' }}
                >
                  <Download size={12} className="me-1" />Download CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setImportText(''); setImportPreview([]); setImportError(''); setShowImportModal(true); }}
                  style={{ fontSize: '0.75rem' }}
                >
                  <Upload size={12} className="me-1" />Import
                </Button>
                <Button variant="primary" size="sm" onClick={() => { setAddValue(''); setAddError(''); setShowAddModal(true); }} disabled={isSaving}>
                  <PlusCircle size={14} className="me-1" /> Add
                </Button>
              </div>
            </div>

            {/* Entries Table */}
            {filteredEntries.length === 0 ? (
              <Alert variant="light" className="text-center py-4">
                {entries.length === 0
                  ? 'Block list is empty. Add emails or domains to prevent sending to them.'
                  : 'No entries match your search.'}
              </Alert>
            ) : (
              <Table hover responsive size="sm" className="align-middle">
                <thead>
                  <tr style={{ fontSize: '0.8rem' }}>
                    <th style={{ width: 36 }}></th>
                    <th>Email / Domain</th>
                    <th style={{ width: 80 }}>Type</th>
                    <th>Date Added</th>
                    <th style={{ width: 150 }}>Source</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry) => (
                    <tr key={entry.id} style={{ fontSize: '0.82rem' }}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selected.has(entry.id)}
                          onChange={() => toggleSelect(entry.id)}
                        />
                      </td>
                      <td className="fw-semibold">{entry.value}</td>
                      <td>
                        <Badge bg={entry.type === 'domain' ? 'info' : 'secondary'} style={{ fontSize: '0.65rem' }}>
                          {entry.type}
                        </Badge>
                      </td>
                      <td className="text-muted">{formatDate(entry.addedAt)}</td>
                      <td>
                        <Badge
                          bg={getBlockSourceBadgeVariant(entry.source)}
                          text={getBlockSourceBadgeVariant(entry.source) === 'light' ? 'dark' : undefined}
                          style={{ fontSize: '0.6rem', border: getBlockSourceBadgeVariant(entry.source) === 'light' ? '1px solid #dee2e6' : undefined }}
                        >
                          {getBlockSourceLabel(entry.source)}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteSingle(entry.id)}
                          style={{ padding: '2px 6px' }}
                          disabled={isSaving}
                        >
                          <Trash size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {isSaving && (
              <div className="text-muted small mt-2">
                <Spinner size="sm" animation="border" className="me-1" /> Saving...
              </div>
            )}
          </div>
        )}

        {activeTab === 'triggers' && (
          <div className="p-3" style={{ maxWidth: 640 }}>
            <h6 className="mb-3">AI Blocklist Triggers</h6>

            {/* Auto-block unsubscribes */}
            <div className="border rounded p-3 mb-3">
              <Form.Check
                type="switch"
                id="auto-block-unsubs"
                label={<span className="fw-semibold">Add unsubscribes to blocklist</span>}
                checked={settings.autoBlockUnsubscribes}
                onChange={(e) => persistSettings({ ...settings, autoBlockUnsubscribes: e.target.checked })}
              />
              <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                When a lead unsubscribes, automatically add their email to the block list.
              </Form.Text>
            </div>

            {/* Block based on lead status */}
            <div className="border rounded p-3 mb-3">
              <div className="fw-semibold mb-2">Also block leads</div>
              <Form.Text className="text-muted d-block mb-2" style={{ fontSize: '0.75rem' }}>
                If a lead is marked as any of the following statuses, add them to the block list.
              </Form.Text>
              <div className="d-flex flex-wrap gap-1 mb-2">
                {settings.blockLeadStatuses.map((status) => (
                  <Badge
                    key={status}
                    bg="primary"
                    style={{ fontSize: '0.72rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={() => persistSettings({ ...settings, blockLeadStatuses: settings.blockLeadStatuses.filter((s) => s !== status) })}
                  >
                    {status} <XCircle size={10} />
                  </Badge>
                ))}
              </div>
              <Form.Select
                size="sm"
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && !settings.blockLeadStatuses.includes(val)) {
                    persistSettings({ ...settings, blockLeadStatuses: [...settings.blockLeadStatuses, val] });
                  }
                }}
                style={{ fontSize: '0.8rem', maxWidth: 300 }}
              >
                <option value="">Add a lead status...</option>
                {DEFAULT_LEAD_STATUSES.filter((s) => !settings.blockLeadStatuses.includes(s)).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Form.Select>
              <InputGroup size="sm" className="mt-2" style={{ maxWidth: 300 }}>
                <Form.Control
                  type="text"
                  placeholder="Custom status..."
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const trimmed = customStatus.trim();
                      if (trimmed && !settings.blockLeadStatuses.includes(trimmed)) {
                        persistSettings({ ...settings, blockLeadStatuses: [...settings.blockLeadStatuses, trimmed] });
                      }
                      setCustomStatus('');
                    }
                  }}
                />
                <Button
                  variant="outline-secondary"
                  disabled={!customStatus.trim()}
                  onClick={() => {
                    const trimmed = customStatus.trim();
                    if (trimmed && !settings.blockLeadStatuses.includes(trimmed)) {
                      persistSettings({ ...settings, blockLeadStatuses: [...settings.blockLeadStatuses, trimmed] });
                    }
                    setCustomStatus('');
                  }}
                >
                  Add
                </Button>
              </InputGroup>
            </div>

            {/* Block based on reply text */}
            <div className="border rounded p-3 mb-3">
              <div className="fw-semibold mb-1">OR</div>
              <Form.Text className="text-muted d-block mb-2" style={{ fontSize: '0.75rem' }}>
                If reply contains the following text, add the lead to the block list.
              </Form.Text>
              <Form.Control
                type="text"
                size="sm"
                placeholder="e.g. remove me, stop emailing, unsubscribe"
                value={settings.blockReplyText}
                onChange={(e) => setSettings({ ...settings, blockReplyText: e.target.value })}
                onBlur={() => persistSettings(settings)}
                style={{ fontSize: '0.8rem', maxWidth: 400 }}
              />
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={() => persistSettings(settings)}
              disabled={isSaving}
            >
              {isSaving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : 'Save'}
            </Button>
          </div>
        )}
      </Container>

      {/* Add Entry Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Add to Block List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="small fw-semibold">Email or Domain</Form.Label>
            <Form.Control
              type="text"
              value={addValue}
              onChange={(e) => { setAddValue(e.target.value); setAddError(''); }}
              placeholder="e.g. spam@example.com or example.com"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            />
            {addError && <Form.Text className="text-danger">{addError}</Form.Text>}
          </Form.Group>
          <Form.Text className="text-muted d-block mt-2" style={{ fontSize: '0.72rem' }}>
            Adding a domain blocks all emails from that domain.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleAdd} disabled={!addValue.trim()}>Add</Button>
        </Modal.Footer>
      </Modal>

      {/* Import Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Import Block List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()} style={{ fontSize: '0.8rem' }}>
              <Upload size={12} className="me-1" /> Upload CSV File
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFileImport} style={{ display: 'none' }} />
            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.72rem' }}>
              CSV with one email/domain per line (or comma-separated). Headers are auto-skipped.
            </Form.Text>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Or paste emails/domains below</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="one@example.com&#10;two@example.com&#10;baddomain.com"
              style={{ fontSize: '0.8rem' }}
            />
            <Button
              variant="outline-secondary"
              size="sm"
              className="mt-2"
              onClick={handlePasteImport}
              disabled={!importText.trim()}
              style={{ fontSize: '0.75rem' }}
            >
              Parse
            </Button>
          </Form.Group>

          {importError && <Alert variant="warning" className="py-2 small">{importError}</Alert>}

          {importPreview.length > 0 && (
            <div>
              <div className="fw-semibold small mb-1">Preview ({importPreview.length} new entries):</div>
              <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.78rem', background: '#f8f9fa', borderRadius: 4, padding: 8 }}>
                {importPreview.slice(0, 50).map((v, i) => (
                  <div key={i}>{v}</div>
                ))}
                {importPreview.length > 50 && (
                  <div className="text-muted">...and {importPreview.length - 50} more</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowImportModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleImportConfirm} disabled={importPreview.length === 0}>
            Import {importPreview.length > 0 ? `(${importPreview.length})` : ''}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
